import { useState, useEffect, useRef } from 'react'
import { ShieldAlert, Lock, X, Terminal } from 'lucide-react'

/**
 * Modal de escalada de privilégios.
 * mode='elevation' → pede confirmação para rodar com sudo
 * mode='password'  → pede a senha sudo
 */
export default function TerminalAISuModal({ mode, command, onConfirm, onCancel }) {
  const [password, setPassword] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (mode === 'password') {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [mode])

  const handleConfirm = () => {
    if (mode === 'password' && !password.trim()) return
    onConfirm(mode === 'password' ? password : undefined)
    setPassword('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleConfirm()
    if (e.key === 'Escape') onCancel()
  }

  return (
    // Backdrop
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="w-[360px] max-w-[90%] rounded-xl border border-slate-700/60 bg-slate-900 shadow-2xl">
        {/* Header */}
        <div className={`flex items-center gap-3 rounded-t-xl px-4 py-3 border-b border-slate-700/60 ${
          mode === 'elevation' ? 'bg-amber-900/20' : 'bg-slate-800/60'
        }`}>
          <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
            mode === 'elevation' ? 'bg-amber-900/50' : 'bg-slate-700'
          }`}>
            {mode === 'elevation'
              ? <ShieldAlert className="h-4 w-4 text-amber-400" />
              : <Lock className="h-4 w-4 text-slate-400" />
            }
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-200">
              {mode === 'elevation' ? 'Permissão negada' : 'Autenticação necessária'}
            </p>
            <p className="text-xs text-slate-400">
              {mode === 'elevation'
                ? 'Este comando requer privilégios elevados'
                : 'O sudo solicitou autenticação'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="rounded p-1 text-slate-500 hover:text-slate-300 transition"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-3">
          {/* Preview do comando */}
          <div className="flex items-start gap-2 rounded-lg bg-slate-800/60 border border-slate-700/40 px-3 py-2">
            <Terminal className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
            <code className="text-xs font-mono text-slate-300 break-all">{command}</code>
          </div>

          {mode === 'elevation' ? (
            <p className="text-xs text-slate-400">
              O comando retornou <span className="text-amber-400 font-medium">permission denied</span>.
              Deseja executar novamente com <code className="text-emerald-400 font-mono text-[11px]">sudo</code>?
            </p>
          ) : (
            <div className="space-y-1.5">
              <label className="text-xs text-slate-400">
                Senha do <code className="text-emerald-400 font-mono text-[11px]">sudo</code>:
              </label>
              <input
                ref={inputRef}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Digite a senha..."
                className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:border-theme-primary focus:outline-none"
                autoComplete="current-password"
              />
              <p className="text-[10px] text-slate-500">
                A senha não é armazenada. Usada somente para esta execução.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 pb-4">
          <button
            onClick={onCancel}
            className="rounded-lg border border-slate-600 bg-slate-800 px-4 py-1.5 text-xs font-medium text-slate-300 transition hover:bg-slate-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={mode === 'password' && !password.trim()}
            className="rounded-lg bg-amber-600 px-4 py-1.5 text-xs font-medium text-white transition hover:bg-amber-500 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {mode === 'elevation' ? 'Executar com sudo' : 'Autenticar'}
          </button>
        </div>
      </div>
    </div>
  )
}
